<template>
  <div>
    <div
      v-for="(page, i) in pages"
      :ref="`page-${i}`"
      :key="`page-${i}`"
      class="coverpage page"
      :class="{ print: isPrinting }"
    >
      <template v-if="i === 0">
        <div class="header" />
        <div
          v-if="resume.candidate"
          class="user-info"
          :class="{
            selected: currentCategoryId === 'candidateInfo',
            simplify: !resume.candidate.email && !resume.candidate.phoneNumber && !resume.candidate.address,
          }"
          @click="selectCategory('candidateInfo')"
        >
          <div class="name">
            {{ resume.candidate.firstname }}
            {{ hideLastname ? '' : resume.candidate.lastname }}
          </div>
          <div class="job" v-html="resume.candidate ? resume.candidate.jobTitle : ''"></div>
        </div>
        <div
          class="more-user-info"
          :class="{
            selected: currentCategoryId === 'candidateInfo',
            none:
              !resume.candidate.email &&
              !resume.candidate.phoneNumber &&
              !resume.candidate.address &&
              !resume.candidate.referenceLink,
          }"
          @click="selectCategory('candidateInfo')"
        >
          <div class="link">{{ resume.candidate.referenceLink }}</div>
          <div class="email" :class="{ hidden: !resume.candidate.email }">
            <span>{{ resume.candidate.email }}</span>
          </div>
          <div class="phone" :class="{ hidden: !resume.candidate.phoneNumber }">
            <span>{{ resume.candidate.phoneNumber }}</span>
          </div>
          <div class="address" :class="{ hidden: !resume.candidate.address }">
            <span>{{ resume.candidate.address }}</span>
          </div>
        </div>
        <div
          class="section convictions"
          :class="{
            selected: currentCategoryId === 'conviction',
            hidden: !convictions || convictions.content.hidden,
            none: !resume.candidate.phoneNumber && !resume.candidate.email && !resume.candidate.address,
          }"
          @click="selectCategory('conviction')"
        >
          <div class="part-title">
            <div class="title">{{ getCategorieTitle('conviction') }}</div>
          </div>
          <div class="data">
            <ConvictionItem v-if="convictions" :content="convictions.content" />
          </div>
        </div>
      </template>
      <div v-if="page.skills.length > 0" class="section skills" @click="selectCategory('skills')">
        <div class="part-title">
          <div v-if="page.skills.length" class="title">
            {{ getCategorieTitle('skills') }}
          </div>
        </div>
        <div class="data">
          <div
            ref="skillGrid"
            class="grid-container"
            :class="{
              selected: currentCategoryId === 'skills',
              'first-column': !skills.find(s => s.content.values),
              'last-column': !skills.find(s => s.content.name),
            }"
          >
            <template v-for="skill in page.skills" :key="skill.id + 'arrow'">
              <div
                v-if="skill.content.name"
                :key="skill.id + 'name'"
                :ref="`skillName-${i}`"
                class="name skillName"
                v-html="`<span>${skill.content.name}</span>`"
              ></div>
              <div class="arrow" :class="{ visible: skill.content.name && skill.content.values }"></div>
              <div
                v-if="skill.content.values"
                :key="skill.id + 'values'"
                class="values"
                v-html="`<span>${skill.content.values}</span>`"
              ></div>
            </template>
          </div>
        </div>
      </div>
      <div v-if="formations && formations.length" class="section formations">
        <div class="part-title">
          <div class="title">{{ getCategorieTitle('formations') }}</div>
        </div>
        <div class="data">
          <Formation
            v-for="formation in formations"
            :key="formation.id"
            :ref="`${formation.id}`"
            :is-selected="currentItemId === formation.id"
            :item="formation"
            :content="formation.content"
            :current-section="currentCategoryId"
            class="item"
            :on-clicked="() => selectItem(formation.id, 'formations')"
          />
        </div>
      </div>
      <div class="spacer end"></div>
      <div class="footer">
        <div class="page-number">{{ i + 1 }}</div>
        <div v-if="resume.contact" class="text">
          {{ resume.contact.companyName }} - {{ resume.contact.phoneNumber }} -
          {{ resume.contact.email }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConvictionItem from '@/components/items/Conviction';
import Formation from '@/components/items/Formation';

export default {
  name: 'CoverPage',

  components: {
    ConvictionItem,
    Formation,
  },

  props: {
    resume: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    titles: {
      type: Object,
      default: () => {},
    },
    selectCategory: {
      type: Function,
      default: () => {},
    },
    selectItem: {
      type: Function,
      default: () => {},
    },
    currentCategoryId: {
      type: String,
      required: true,
    },
    currentItemId: {
      type: String,
      required: true,
    },
    contact: {
      type: String,
      default: '',
    },
    isPrinting: {
      type: Boolean,
    },
  },

  emits: ['nbOfPages'],

  data: function () {
    return {
      pages: [{ skills: [] }],
    };
  },

  computed: {
    convictions() {
      return this.items.find(i => i.type === 'conviction');
    },
    skills() {
      const skills = this.items.filter(i => i.type === 'skills');
      skills.sort((a, b) => a.order - b.order);
      return skills;
    },
    formations() {
      return this.items.filter(i => i.type === 'formations');
    },
    hideLastname() {
      if (
        !this.resume.items.find(i => i.type === 'candidateInfo') ||
        !this.resume.items.find(i => i.type === 'candidateInfo').content
      ) {
        return true;
      } else {
        return !(this.resume.items.find(i => i.type === 'candidateInfo').content.hidden === false);
      }
    },
  },

  watch: {
    async skills() {
      await this.recalculatePages();
    },
  },

  async mounted() {
    setTimeout(this.recalculatePages, 1000);
  },

  methods: {
    async recalculatePages() {
      if (!this.skills.length) return;
      this.pages = [{ skills: [] }];

      let currentPage = 0;
      this.skills.forEach(skill => {
        if (skill.content.startNewPage) {
          currentPage++;
          this.pages.push({ skills: [] });
        }
        this.pages[currentPage].skills.push(skill);
      });
      await this.$nextTick();

      for (let i = 0; i < this.pages.length; i++) {
        const currentPageElement = this.$refs[`page-${i}`][0];

        if (currentPageElement.clientHeight < currentPageElement.scrollHeight) {
          if (i + 1 === this.pages.length) {
            this.pages.push({ skills: [] });
          }

          do {
            const lastSkill = this.pages[i].skills.pop();
            this.pages[i + 1].skills.unshift(lastSkill);

            await this.$nextTick();
          } while (currentPageElement.clientHeight < currentPageElement.scrollHeight);
        }
      }

      this.$emit('nbOfPages', this.pages.length);
    },

    getCategorieTitle(categorieId) {
      const titles = this.titles;
      if (titles && titles[categorieId]) {
        return titles[categorieId];
      }

      return this.$t(`resume.sections.${this.resume.language || 'fr'}.${categorieId}`);
    },
  },
};
</script>

<style scoped lang="scss">
.page,
.coverpage {
  -webkit-print-color-adjust: exact;
  width: 21cm;
  height: 29.69cm;
  background-color: white;
  margin: 24px auto;
  box-shadow:
    0 2px 8px -2px rgba(0, 0, 0, 0.1),
    0 4px 12px -4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  &.print {
    margin: 0;
    border-radius: 0;
    box-shadow: none;
  }
  .item,
  .part-title {
    flex-shrink: 0;
  }
  .spacer {
    &:before {
      content: '';
      display: block;
      width: 1px;
      height: 0.5cm;
      margin-left: calc(4cm - 1px);
    }
    &.end {
      flex-grow: 1;
      &:before {
        height: 100%;
      }
    }
  }
}
.user-info:hover,
.user-info.selected {
  cursor: pointer;
  background-color: #fff9ef !important;
  border-radius: 12px;
}
table:hover,
.grid-container.selected {
  background-color: #fff9ef !important;
  cursor: pointer;
  border-radius: 12px;
}
.convictions.selected .data {
  background-color: #fff9ef !important;
  cursor: pointer;
  border-radius: 12px;
}
.more-user-info,
.convictions .data,
.skills .data {
  &:hover,
  &.selected {
    background-color: #fff9ef !important;
    cursor: pointer;
    border-radius: 12px;
  }
}

.data {
  padding: 16px 0 0 0 !important;
}
</style>
